import React, { useState, useEffect } from 'react';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  serverTimestamp, 
  doc, 
  onSnapshot, 
  query, 
  updateDoc, 
  deleteDoc 
} from 'firebase/firestore';
import { 
  getAuth, 
  onAuthStateChanged, 
  signInAnonymously, 
  signInWithCustomToken 
} from 'firebase/auth';
import { 
  ShoppingCart, 
  CheckSquare, 
  CreditCard,
  Package,
  Send,
  AlertCircle,
  Building2,
  UserCircle,
  Search,
  Settings,
  Save,
  LayoutDashboard,
  Users,
  AlertTriangle,
  Clock,
  FileQuestion,
  Plus,
  Edit,
  Trash2,
  X,
  Mail,
  Scale,
  ChevronRight,
  UserPlus,
  CheckCircle2,
  Euro,
  FileText,
  Tag,
  Boxes
} from 'lucide-react';

// --- Firebase Initialisierung ---
const getGlobal = (key) => {
  if (typeof window !== 'undefined' && window[key] !== undefined) {
    return window[key];
  }
  return null;
};

const firebaseConfigRaw = getGlobal('__firebase_config');
const firebaseConfig = firebaseConfigRaw ? JSON.parse(firebaseConfigRaw) : {};

// Nutzt die bereits existierende App-Instanz, um Sitzungskonflikte zu vermeiden
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
const auth = getAuth(app);
const db = getFirestore(app);

// Die appId für die Firestore-Pfade (Regel 1)
const appId = getGlobal('__app_id') || 'meetform-purchasing';

// ============================================================================
// DASHBOARD COMPONENT
// ============================================================================
const PurchasingDashboard = () => (
  <div className="max-w-6xl mx-auto space-y-6 animate-fadeIn">
    <div className="flex items-center gap-3 mb-8">
      <LayoutDashboard className="w-8 h-8 text-[#0F52BA]" />
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Einkaufs-Dashboard</h2>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-red-100 dark:border-red-900/30 hover:shadow-md transition-all">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-red-100 text-red-600 rounded-xl dark:bg-red-900/50 dark:text-red-400">
            <Package className="w-6 h-6"/>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white">Nicht geliefert</h3>
            <p className="text-sm text-gray-500">Überfällige Bestellungen</p>
          </div>
        </div>
        <div className="text-4xl font-extrabold text-gray-900 dark:text-white">12</div>
        <div className="mt-4 text-sm text-red-600 font-medium cursor-pointer hover:underline">Details ansehen &rarr;</div>
      </div>
      
      <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-yellow-100 dark:border-yellow-900/30 hover:shadow-md transition-all">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-yellow-100 text-yellow-600 rounded-xl dark:bg-yellow-900/50 dark:text-yellow-400">
            <FileQuestion className="w-6 h-6"/>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white">Offene Anfragen</h3>
            <p className="text-sm text-gray-500">Noch kein Angebot</p>
          </div>
        </div>
        <div className="text-4xl font-extrabold text-gray-900 dark:text-white">5</div>
        <div className="mt-4 text-sm text-yellow-600 font-medium cursor-pointer hover:underline">Details ansehen &rarr;</div>
      </div>

      <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-blue-100 dark:border-blue-900/30 hover:shadow-md transition-all">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-blue-100 text-[#0F52BA] rounded-xl dark:bg-blue-900/50 dark:text-blue-400">
            <FileText className="w-6 h-6"/>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white">Fehlende Doks</h3>
            <p className="text-sm text-gray-500">Zertifikate, NDAs</p>
          </div>
        </div>
        <div className="text-4xl font-extrabold text-gray-900 dark:text-white">8</div>
        <div className="mt-4 text-sm text-[#0F52BA] font-medium cursor-pointer hover:underline">Details ansehen &rarr;</div>
      </div>
    </div>
  </div>
);

// ============================================================================
// PLATZHALTER FÜR ANFRAGEN & BESTELLUNGEN
// ============================================================================
const PlaceholderView = ({ title, icon: Icon, desc }) => (
  <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border dark:border-gray-700">
    <div className="p-6 bg-gray-50 dark:bg-gray-900 rounded-full mb-6">
      <Icon className="w-16 h-16 text-gray-400" />
    </div>
    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{title}</h2>
    <p className="text-gray-500 max-w-md">{desc}</p>
  </div>
);

// ============================================================================
// LIEFERANTENVERWALTUNG (Suppliers Component)
// ============================================================================
const initialSupplierState = {
  supplierId: '',
  customerNumber: '', // NEU: Unsere Kundennummer beim Lieferanten
  companyName: '',
  street: '',
  zip: '',
  city: '',
  country: '',
  phone: '',
  website: '',
  minOrderValue: '',
  emails: [{ address: '', label: 'Anfrage', type: 'to' }],
  contacts: [{ 
    salutation: '', 
    firstName: '', 
    lastName: '', 
    position: '', 
    phone: '', 
    email: '', 
    category: '' 
  }],
  categories: [],
  vatId: '',
  taxId: '',
  iban: '',
  bic: '',
  bankName: '',
  paymentTerms: '',
  incoterms: ''
};

export const Suppliers = ({ userId, companyId }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [formData, setFormData] = useState(initialSupplierState);
  
  // NEU: Getrennte Suchfelder
  const [searchNameId, setSearchNameId] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  
  const [feedback, setFeedback] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeUser, setActiveUser] = useState(null);
  const [newCategory, setNewCategory] = useState('');

  // Authentifizierung
  useEffect(() => {
    if (userId) {
      setActiveUser({ uid: userId });
      return;
    }
    const initAuth = async () => {
      if (auth.currentUser) {
        setActiveUser(auth.currentUser);
        return;
      }
      try {
        const token = getGlobal('__initial_auth_token');
        if (token) {
          await signInWithCustomToken(auth, token);
        } else {
          await signInAnonymously(auth);
        }
      } catch (err) {
        console.error("Auth Initialization Error:", err);
      }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setActiveUser(u);
    });
    return () => unsubscribe();
  }, [userId]);

  // Daten laden
  useEffect(() => {
    if (!activeUser) return;
    const suppliersRef = collection(db, 'companies', companyId, 'suppliers');
    const unsubscribe = onSnapshot(suppliersRef, 
      (snapshot) => {
        const loaded = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSuppliers(loaded);
      }, 
      (error) => console.error("Firestore Permission Error:", error)
    );
    return () => unsubscribe();
  }, [activeUser, companyId]);

  const generateNextId = () => {
    if (suppliers.length === 0) return "10000";
    const ids = suppliers.map(s => parseInt(s.supplierId)).filter(id => !isNaN(id));
    const maxId = ids.length > 0 ? Math.max(...ids) : 9999;
    return (maxId + 1).toString();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateEmail = (index, field, value) => {
    const updatedEmails = [...formData.emails];
    updatedEmails[index][field] = value;
    setFormData(prev => ({ ...prev, emails: updatedEmails }));
  };

  const handleUpdateContact = (index, field, value) => {
    const updated = [...formData.contacts];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, contacts: updated }));
  };

  const handleAddCategory = () => {
    if (newCategory.trim() !== '') {
      setFormData(prev => ({ 
        ...prev, 
        categories: [...(prev.categories || []), newCategory.trim()] 
      }));
      setNewCategory('');
    }
  };

  const handleRemoveCategory = (index) => {
    setFormData(prev => ({
      ...prev,
      categories: (prev.categories || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddNew = () => {
    const nextId = generateNextId();
    setFormData({ ...initialSupplierState, supplierId: nextId });
    setCurrentId(null);
    setIsEditing(true);
    setFeedback(null);
    setNewCategory('');
  };

  const handleSave = async () => {
    if (!activeUser) {
      setFeedback({ type: 'error', text: 'Keine aktive Sitzung.' });
      return;
    }
    if (!formData.companyName) {
      setFeedback({ type: 'error', text: 'Bitte Firmennamen angeben.' });
      return;
    }

    setIsSaving(true);
    setFeedback(null);
    try {
      const supplierData = { 
        ...formData, 
        updatedAt: serverTimestamp(),
        createdBy: activeUser.uid 
      };
      
      const collectionRef = collection(db, 'companies', companyId, 'suppliers');

      if (currentId) {
        const docRef = doc(db, 'companies', companyId, 'suppliers', currentId);
        await updateDoc(docRef, supplierData);
        setFeedback({ type: 'success', text: 'Erfolgreich aktualisiert!' });
      } else {
        await addDoc(collectionRef, { ...supplierData, createdAt: serverTimestamp() });
        setFeedback({ type: 'success', text: 'Lieferant wurde erfolgreich angelegt!' });
      }
      
      setTimeout(() => {
        setIsEditing(false);
        setFeedback(null);
      }, 2000);

    } catch (error) {
      console.error("Firestore Speicherfehler:", error);
      setFeedback({ type: 'error', text: `Speicherfehler: ${error.message}` });
    } finally {
      setIsSaving(false);
    }
  };

  // NEU: Filter-Logik für getrennte Suchfelder
  const filtered = suppliers.filter(s => {
    const termNameId = searchNameId.toLowerCase();
    const termCategory = searchCategory.toLowerCase();
    
    const matchesNameId = termNameId === '' || 
      (s.companyName || '').toLowerCase().includes(termNameId) ||
      (s.supplierId || '').toLowerCase().includes(termNameId);
      
    const matchesCat = termCategory === '' || 
      (s.categories || []).some(c => c.toLowerCase().includes(termCategory));
      
    return matchesNameId && matchesCat;
  });

  if (isEditing) {
    return (
      <div className="max-w-5xl mx-auto space-y-6 animate-fadeIn pb-10">
        <div className="flex justify-between items-center bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border dark:border-gray-700 sticky top-0 z-10">
          <h2 className="text-xl font-bold flex items-center gap-2 text-gray-900 dark:text-white">
            <Building2 className="w-6 h-6 text-[#0F52BA]" />
            {currentId ? 'Lieferant bearbeiten' : 'Neuer Lieferant'}
          </h2>
          <div className="flex gap-2">
            <button onClick={() => setIsEditing(false)} className="px-4 py-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">Abbrechen</button>
            <button onClick={handleSave} disabled={isSaving} className="px-6 py-2 bg-[#0F52BA] text-white rounded-lg font-bold flex items-center gap-2 shadow-lg hover:bg-blue-800 transition-all disabled:opacity-50">
              <Save className="w-4 h-4" /> {isSaving ? 'Speichert...' : 'Speichern'}
            </button>
          </div>
        </div>

        {feedback && (
          <div className={`p-4 rounded-xl flex items-center gap-3 transition-all animate-slideDown ${feedback.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
            {feedback.type === 'error' ? <AlertCircle className="w-5 h-5"/> : <CheckCircle2 className="w-5 h-5"/>}
            <span className="font-medium">{feedback.text}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border dark:border-gray-700 space-y-6">
            <section>
              <h3 className="font-bold text-[#0F52BA] border-b pb-2 flex items-center gap-2 mb-4"><Building2 className="w-4 h-4"/> 1. Allgemeine Daten</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase font-bold text-gray-400">Lieferanten-ID</label>
                  <input type="text" name="supplierId" value={formData.supplierId} readOnly className="w-full p-2 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-lg text-sm font-mono" />
                </div>
                <div>
                  {/* NEU: Kundennummer */}
                  <label className="text-[10px] uppercase font-bold text-gray-400">Unsere Kundennummer</label>
                  <input type="text" name="customerNumber" value={formData.customerNumber || ''} onChange={handleInputChange} className="w-full p-2 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-lg text-sm font-mono dark:text-white" placeholder="Optionale Kd-Nr." />
                </div>
                <div className="col-span-2 mt-2">
                  <label className="text-[10px] uppercase font-bold text-gray-400">Firmenname *</label>
                  <input type="text" name="companyName" value={formData.companyName} onChange={handleInputChange} className="w-full p-2 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-lg text-sm font-bold dark:text-white" />
                </div>
              </div>
              <div className="mt-4">
                <label className="text-[10px] uppercase font-bold text-gray-400">Anschrift</label>
                <input type="text" name="street" value={formData.street} onChange={handleInputChange} className="w-full p-2 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-lg text-sm mb-2 dark:text-white" placeholder="Straße" />
                <div className="grid grid-cols-3 gap-2">
                  <input type="text" name="zip" value={formData.zip} onChange={handleInputChange} className="p-2 border dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white" placeholder="PLZ" />
                  <input type="text" name="city" value={formData.city} onChange={handleInputChange} className="col-span-2 p-2 border dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white" placeholder="Ort" />
                </div>
              </div>
            </section>
            
            <section className="pt-4 border-t dark:border-gray-700">
              <h3 className="text-[10px] uppercase font-bold text-gray-400 mb-2 tracking-wider flex items-center gap-1"><Tag className="w-3 h-3"/> Rubriken (Kategorien)</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {(formData.categories || []).map((cat, idx) => (
                  <span key={idx} className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                    {cat}
                    <button type="button" onClick={() => handleRemoveCategory(idx)} className="hover:text-red-500 bg-blue-200 dark:bg-blue-800 rounded-full p-0.5 ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={newCategory} 
                  onChange={e => setNewCategory(e.target.value)} 
                  onKeyDown={e => { if(e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }}
                  className="flex-1 p-2 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-lg text-sm dark:text-white" 
                  placeholder="Z.B. Hardware, Rohstoffe..." 
                />
                <button type="button" onClick={handleAddCategory} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-white text-sm font-bold rounded-lg transition-colors">
                  Hinzufügen
                </button>
              </div>
            </section>

            <section className="pt-4 border-t dark:border-gray-700">
              <h3 className="text-[10px] uppercase font-bold text-gray-400 mb-4 tracking-wider">E-Mail Adressen</h3>
              <div className="space-y-2">
                {formData.emails.map((m, i) => (
                  <div key={i} className="flex gap-2 items-center bg-gray-50 dark:bg-gray-900 p-2 rounded border dark:border-gray-700">
                    <input value={m.label} onChange={e => handleUpdateEmail(i, 'label', e.target.value)} className="w-28 p-2 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded text-xs font-bold dark:text-white" placeholder="Zweck..." />
                    <input value={m.address} onChange={e => handleUpdateEmail(i, 'address', e.target.value)} className="flex-1 p-2 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded text-sm dark:text-white" placeholder="E-Mail" />
                    <select value={m.type || 'to'} onChange={e => handleUpdateEmail(i, 'type', e.target.value)} className="w-24 p-2 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded text-xs dark:text-white">
                      <option value="to">An</option>
                      <option value="cc">Kopie (CC)</option>
                      <option value="bcc">BCC</option>
                    </select>
                    <button type="button" onClick={() => setFormData({...formData, emails: formData.emails.filter((_,idx) => idx !== i)})} className="text-red-500 p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"><Trash2 className="w-4 h-4"/></button>
                  </div>
                ))}
                <button type="button" onClick={() => setFormData(p => ({...p, emails: [...p.emails, {address: '', label: 'Neu', type: 'to'}]}))} className="text-sm text-[#0F52BA] font-bold flex items-center gap-1 mt-2 hover:underline"><Plus className="w-4 h-4"/> E-Mail hinzufügen</button>
              </div>
            </section>

            <section className="pt-4 border-t dark:border-gray-700">
              <h3 className="font-bold text-[#0F52BA] mb-4 flex items-center gap-2"><Users className="w-4 h-4"/> Ansprechpartner</h3>
              <div className="space-y-4">
                {formData.contacts.map((c, i) => (
                  <div key={i} className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border dark:border-gray-700 space-y-3 relative">
                    <div className="flex justify-between items-center mb-2">
                      <input 
                        value={c.category} 
                        onChange={e => handleUpdateContact(i, 'category', e.target.value)} 
                        className="flex-1 mr-4 p-2 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded text-xs font-bold text-[#0F52BA]" 
                        placeholder="Zuständigkeit (z.B. Vertrieb, Technik)" 
                      />
                      <button type="button" onClick={() => setFormData({...formData, contacts: formData.contacts.filter((_,idx) => idx !== i)})} className="flex items-center gap-1 text-xs font-bold text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/50 px-2 py-1 rounded transition-colors">
                        <Trash2 className="w-3 h-3"/> Löschen
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <select 
                        value={c.salutation} 
                        onChange={e => handleUpdateContact(i, 'salutation', e.target.value)} 
                        className="p-2 border dark:border-gray-700 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                      >
                        <option value="">Anrede...</option>
                        <option value="Herr">Herr</option>
                        <option value="Frau">Frau</option>
                        <option value="Divers">Divers</option>
                      </select>
                      <input value={c.firstName} onChange={e => handleUpdateContact(i, 'firstName', e.target.value)} className="p-2 border dark:border-gray-700 rounded text-sm bg-white dark:bg-gray-800 dark:text-white" placeholder="Vorname" />
                      <input value={c.lastName} onChange={e => handleUpdateContact(i, 'lastName', e.target.value)} className="p-2 border dark:border-gray-700 rounded text-sm bg-white dark:bg-gray-800 dark:text-white" placeholder="Nachname" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={c.phone} onChange={e => handleUpdateContact(i, 'phone', e.target.value)} className="p-2 border dark:border-gray-700 rounded text-sm bg-white dark:bg-gray-800 dark:text-white" placeholder="Telefon" />
                      <input value={c.email} onChange={e => handleUpdateContact(i, 'email', e.target.value)} className="p-2 border dark:border-gray-700 rounded text-sm bg-white dark:bg-gray-800 dark:text-white" placeholder="Direkte E-Mail" />
                    </div>
                  </div>
                ))}
                <button type="button" onClick={() => setFormData(p => ({...p, contacts: [...p.contacts, {salutation:'', firstName:'', lastName:'', category:''}]}))} className="text-sm text-[#0F52BA] font-bold flex items-center gap-1 hover:underline"><UserPlus className="w-4 h-4"/> Ansprechpartner hinzufügen</button>
              </div>
            </section>
          </div>

          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border dark:border-gray-700 space-y-4">
              <h3 className="font-bold text-[#0F52BA] border-b pb-2 flex items-center gap-2"><CreditCard className="w-4 h-4"/> 2. Bankdaten</h3>
              <div className="space-y-3">
                <input type="text" name="iban" value={formData.iban} onChange={handleInputChange} className="w-full p-2 border dark:border-gray-600 rounded text-sm font-mono dark:bg-gray-700 dark:text-white" placeholder="IBAN" />
                <input type="text" name="bic" value={formData.bic} onChange={handleInputChange} className="w-full p-2 border dark:border-gray-600 rounded text-sm font-mono dark:bg-gray-700 dark:text-white" placeholder="BIC" />
                <input type="text" name="bankName" value={formData.bankName} onChange={handleInputChange} className="w-full p-2 border dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white" placeholder="Name der Bank" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border dark:border-gray-700 space-y-4">
              <h3 className="font-bold text-[#0F52BA] border-b pb-2 flex items-center gap-2"><ShoppingCart className="w-4 h-4"/> 3. Konditionen</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase font-bold text-gray-400">Mindestbestellwert (€)</label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input type="number" name="minOrderValue" value={formData.minOrderValue} onChange={handleInputChange} className="w-full p-2 pl-9 border dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white" placeholder="0.00" />
                  </div>
                </div>
                <input type="text" name="paymentTerms" value={formData.paymentTerms} onChange={handleInputChange} className="w-full p-2 border dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white" placeholder="Zahlungsbedingungen" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fadeIn">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border dark:border-gray-700">
        <h2 className="text-xl font-bold flex items-center gap-2 text-gray-900 dark:text-white whitespace-nowrap"><Building2 className="w-6 h-6 text-[#0F52BA]" /> Lieferanten</h2>
        
        {/* NEU: Getrennte Suchfelder und Layout */}
        <div className="flex w-full md:w-auto gap-3 flex-col sm:flex-row flex-1 md:justify-end">
          <div className="relative flex-1 md:max-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input 
              type="text" 
              placeholder="Firma oder ID..." 
              value={searchNameId} 
              onChange={e => setSearchNameId(e.target.value)} 
              className="w-full pl-9 pr-4 py-2 border dark:border-gray-700 rounded-lg text-sm dark:bg-gray-700 outline-none dark:text-white" 
            />
          </div>
          <div className="relative flex-1 md:max-w-[200px]">
            <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input 
              type="text" 
              placeholder="Nach Rubrik..." 
              value={searchCategory} 
              onChange={e => setSearchCategory(e.target.value)} 
              className="w-full pl-9 pr-4 py-2 border dark:border-gray-700 rounded-lg text-sm dark:bg-gray-700 outline-none dark:text-white" 
            />
          </div>
          <button onClick={handleAddNew} className="bg-[#0F52BA] hover:bg-blue-800 transition-colors text-white px-4 py-2 rounded-lg font-bold flex items-center justify-center gap-2 shadow-sm whitespace-nowrap">
            <Plus className="w-4 h-4"/> Neu anlegen
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map(s => (
          <div key={s.id} className="bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-sm border dark:border-gray-700 hover:shadow-md transition-all flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <span className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">ID: {s.supplierId}</span>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white line-clamp-1" title={s.companyName}>{s.companyName}</h3>
                  <p className="text-xs text-gray-500">{s.city || 'Kein Ort'}</p>
                </div>
                {/* NEU: Anzeige der Kundennummer */}
                {s.customerNumber && (
                  <div className="text-right">
                    <span className="text-[9px] uppercase font-bold text-gray-400">Kd-Nr.</span>
                    <div className="text-sm font-mono font-bold text-[#0F52BA] dark:text-blue-400">{s.customerNumber}</div>
                  </div>
                )}
              </div>

              {/* Anzeige der Rubriken */}
              {(s.categories && s.categories.length > 0) && (
                <div className="flex flex-wrap gap-1 mb-4 mt-2">
                  {s.categories.map((c, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-[10px] uppercase font-bold tracking-wider">
                      {c}
                    </span>
                  ))}
                </div>
              )}
              
              <div className="space-y-3 mb-6 mt-4 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-100 dark:border-gray-700/50">
                <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <Mail className="w-3.5 h-3.5 mt-0.5 text-gray-400"/> 
                  <span className="truncate">{s.emails?.find(e => e.label === 'Anfrage')?.address || s.emails?.[0]?.address || 'Keine E-Mail'}</span>
                </div>
                <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <UserCircle className="w-3.5 h-3.5 mt-0.5 text-gray-400"/> 
                  {/* Anzeige der Ansprechpartner */}
                  <div className="flex flex-col gap-1 w-full">
                    {s.contacts && s.contacts.length > 0 && s.contacts[0].lastName ? (
                      s.contacts.slice(0,2).map((c, idx) => (
                        <div key={idx} className="flex justify-between items-center w-full">
                          <span className="truncate" title={`${c.salutation} ${c.firstName} ${c.lastName}`.trim()}>
                            {c.salutation} {c.firstName} {c.lastName}
                          </span>
                          {c.category && <span className="text-[9px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded ml-2 whitespace-nowrap">{c.category}</span>}
                        </div>
                      ))
                    ) : (
                      <span className="italic text-gray-400">Kein Ansprechpartner</span>
                    )}
                    {(s.contacts && s.contacts.length > 2) && <span className="text-[10px] text-blue-500">+{s.contacts.length - 2} weitere...</span>}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-4 border-t dark:border-gray-700 mt-auto">
              {/* NEU: Sortiment-Button */}
              <button onClick={() => alert(`Sortiment für "${s.companyName}" wird geladen... (Modul in Entwicklung)`)} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-bold transition-colors">
                <Boxes className="w-3.5 h-3.5"/> Sortiment
              </button>
              
              <button onClick={() => { setFormData(s); setCurrentId(s.id); setIsEditing(true); }} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#0F52BA]/10 text-[#0F52BA] hover:bg-[#0F52BA]/20 rounded-lg text-xs font-bold transition-colors">
                <Edit className="w-3.5 h-3.5"/> Bearbeiten
              </button>
              <button onClick={async () => { if(window.confirm('Lieferant unwiderruflich löschen?')) await deleteDoc(doc(db, 'companies', companyId, 'suppliers', s.id)) }} className="p-2 bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 rounded-lg text-xs font-bold transition-colors">
                <Trash2 className="w-4 h-4"/>
              </button>
            </div>
          </div>
        ))}
        {activeUser && filtered.length === 0 && (
          <div className="col-span-full py-16 flex flex-col items-center justify-center text-gray-500 bg-white dark:bg-gray-800 rounded-2xl border border-dashed dark:border-gray-700">
            <Building2 className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-lg font-medium text-gray-600 dark:text-gray-300">Keine Lieferanten gefunden.</p>
            <p className="text-sm">Passen Sie Ihre Suche an oder legen Sie einen neuen an.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN HUB / NAVIGATION
// ============================================================================
export const PurchasingModule = ({ userId, companyId }) => {
  const [activeTab, setActiveTab] = useState('dashboard'); // Standard Tab ist jetzt das Dashboard

  return (
    <div className="h-full flex flex-col md:flex-row w-full animate-fadeIn bg-gray-50 dark:bg-gray-900 rounded-xl overflow-hidden border dark:border-gray-800 shadow-sm">
      
      {/* Seitenleiste (Sidebar) */}
      <aside className="w-full md:w-64 bg-white dark:bg-gray-800 border-r dark:border-gray-700 flex flex-col">
        <div className="p-6 border-b dark:border-gray-700 flex items-center gap-3 text-[#0F52BA] font-bold text-lg">
          <ShoppingCart className="w-6 h-6" /> Einkauf
        </div>
        
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <button 
            onClick={() => setActiveTab('dashboard')} 
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'dashboard' ? 'bg-blue-50 dark:bg-blue-900/50 text-[#0F52BA]' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
          >
            <LayoutDashboard className="w-5 h-5" /> Dashboard
          </button>
          
          <div className="pt-6 pb-2 px-3">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Lieferantenwesen</p>
          </div>
          
          {/* NEU: Lieferanten, Anfragen und Bestellungen alle auf der gleichen Ebene */}
          <button 
            onClick={() => setActiveTab('lieferanten')} 
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'lieferanten' ? 'bg-blue-50 dark:bg-blue-900/50 text-[#0F52BA]' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
          >
            <Building2 className="w-5 h-5" /> Lieferanten
          </button>
          
          <button 
            onClick={() => setActiveTab('anfragen')} 
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'anfragen' ? 'bg-blue-50 dark:bg-blue-900/50 text-[#0F52BA]' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
          >
            <FileQuestion className="w-5 h-5" /> Anfragen
          </button>
          
          <button 
            onClick={() => setActiveTab('bestellungen')} 
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'bestellungen' ? 'bg-blue-50 dark:bg-blue-900/50 text-[#0F52BA]' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
          >
            <Package className="w-5 h-5" /> Bestellungen
          </button>

        </nav>
      </aside>

      {/* Hauptinhalt (Main Content) */}
      <div className="flex-1 p-6 overflow-y-auto">
        {activeTab === 'dashboard' && <PurchasingDashboard />}
        {activeTab === 'lieferanten' && <Suppliers userId={userId} companyId={companyId} />}
        {activeTab === 'anfragen' && <PlaceholderView title="Anfragen-Modul" icon={FileQuestion} desc="Hier können Sie bald gezielte Preisanfragen an Ihre Lieferanten stellen und Angebote verwalten." />}
        {activeTab === 'bestellungen' && <PlaceholderView title="Bestellwesen" icon={Package} desc="Hier entsteht das Bestellmodul zur Auslösung von Aufträgen und zur Überwachung des Wareneingangs." />}
      </div>

    </div>
  );
};

// ============================================================================
// PRICING VIEW (Export für Kompatibilität mit App.js)
// ============================================================================
export const PricingView = () => {
  return (
    <div className="h-full flex-1 p-6 overflow-y-auto">
      <PlaceholderView 
        title="Preiskalkulation" 
        icon={Euro} 
        desc="Dieses Modul befindet sich aktuell im Aufbau." 
      />
    </div>
  );
};